import { useEffect, useRef, useState, ReactElement } from 'react';
import styled from 'styled-components';
import { motion, useAnimation } from 'motion/react';

import { FrostedGlassLight } from '@seed-health/tokens';
import Hero from 'components/library/Hero';
import type { Slide } from 'components/library/Hero';
import { FontFamily } from 'styled/fonts/font-family';

const GlassBackground = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: var(--scrim-glass-dark-08);
  backdrop-filter: ${FrostedGlassLight};
  -webkit-backdrop-filter: ${FrostedGlassLight};
  z-index: 9998;
`;

const LoaderWrapper = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: var(--fill-brand);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ContentWrapper = styled.div<{ $contentWrapperBackground?: string }>`
  background: ${(props) =>
    props.$contentWrapperBackground || 'var(--fill-primary)'};
  width: 100%;
`;

const WordContainer = styled(motion.h1)`
  font-family: ${FontFamily.sans};
  font-weight: 350;
  letter-spacing: -0.025em;
  line-height: 1.1;
  display: flex;
  user-select: none;
  text-align: center;
  color: var(--fill-primary);
  font-size: clamp(3rem, 8vw, 8rem);
  filter: url(#threshold) blur(0.6px);
`;

const MorphElement = styled.span`
  color: inherit;
  display: inline-block;
  font-size: inherit;
  transition: none;
`;

const Circle = styled.span`
  font-size: inherit;
  width: 1ex;
  height: 1ex;
  background-color: #ffffff;
  border-radius: 50%;
  display: inline-block;
`;

const SVGFilter = styled.svg`
  position: absolute;
  width: 0;
  height: 0;
`;

type AnimationElement = {
  type: 'letter' | 'custom';
  content: string | React.ReactNode;
  key: string;
};

interface LoaderProps {
  isVisible?: boolean;
  slides?: Slide[];
  id: string;
  label?: React.ReactNode;
  pillAndTitle?: ReactElement;
  config?: {
    height?: string;
    mobileHeight?: string;
    contentWrapperBackground?: string;
  };
}

const Loader: React.FC<LoaderProps> = ({
  isVisible = true,
  slides = [],
  label,
  config,
  id,
  pillAndTitle,
}) => {
  const rafRef = useRef<number | undefined>(undefined);
  const elementRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const [animationElements, setAnimationElements] = useState<
    AnimationElement[]
  >([]);
  const [randomOrder, setRandomOrder] = useState<number[]>([]);
  const [letterAnimationComplete, setLetterAnimationComplete] =
    useState<boolean>(false);
  const [shouldRemoveLoader, setShouldRemoveLoader] = useState<boolean>(false);

  // Flag to disable animations while keeping all code intact
  const DISABLE_ANIMATIONS = true;

  const elementCompletionRef = useRef<boolean[]>([]);
  const hasCompletedRef = useRef<boolean>(false);
  const controls = useAnimation();
  const wordControls = useAnimation();
  const glassControls = useAnimation();
  const heroControls = useAnimation();

  useEffect(() => {
    if (DISABLE_ANIMATIONS) {
      document.body.classList.remove('has-loader');
    } else {
      document.body.classList.add('has-loader');
    }

    return () => {
      // Clean up on unmount
      document.body.classList.remove('has-loader');
    };
  }, [DISABLE_ANIMATIONS]);

  // Default values
  const word = 'Seed';
  const duration = 150;
  const staggerDelay = 35;

  // Create animation elements when component mounts
  useEffect(() => {
    if (DISABLE_ANIMATIONS) return;

    // Convert word to letters
    const letterElements: AnimationElement[] = word
      .split('')
      .map((char, index) => ({
        type: 'letter' as const,
        content: char === ' ' ? '\u00A0' : char,
        key: `letter-${index}`,
      }));

    // Add custom elements (circle)
    const customElements: AnimationElement[] = [
      {
        type: 'custom' as const,
        content: <Circle />,
        key: 'circle-1',
      },
    ];

    // Combine letters and custom elements into one array to animate
    const allElements = [...letterElements, ...customElements];
    setAnimationElements(allElements);
    elementRefs.current = new Array(allElements.length).fill(null);
    elementCompletionRef.current = new Array(allElements.length).fill(false);
    hasCompletedRef.current = false;
    setLetterAnimationComplete(false);

    // Create randomized order
    const indices = Array.from({ length: allElements.length }, (_, i) => i);
    const shuffled = [...indices].sort(() => Math.random() - 0.5);
    setRandomOrder(shuffled);
  }, [word]);

  useEffect(() => {
    if (!isVisible || animationElements.length === 0) return;

    // Animation parameters
    const morphTime = 2.5;
    const cooldownTime = 0.15;

    let time = new Date();
    let morph = 0;
    let cooldown = duration / 1000; // Convert duration to seconds
    let hasStartedMorphing = false;

    // Reset completion tracking
    elementCompletionRef.current = new Array(animationElements.length).fill(
      false,
    );
    hasCompletedRef.current = false;

    // Initialize all elements (start invisible for fade-in effect)
    elementRefs.current.forEach((elementRef, index) => {
      if (elementRef) {
        elementRef.style.opacity = '0%';
        elementRef.style.filter = 'blur(100px)';
        // Set text content only for letter elements
        if (animationElements[index].type === 'letter') {
          elementRef.textContent = animationElements[index].content as string;
        }
      }
    });

    function checkAllElementsComplete() {
      const allComplete = elementCompletionRef.current.every(
        (completed) => completed,
      );
      if (allComplete && !hasCompletedRef.current) {
        hasCompletedRef.current = true;
        // Cancel the animation frame to stop the loop
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }
        // Set state to trigger subsequent animations
        setLetterAnimationComplete(true);
      }
    }

    function doMorph() {
      if (!hasStartedMorphing) {
        hasStartedMorphing = true;
      }

      morph -= cooldown;
      cooldown = 0;

      let fraction = morph / morphTime;

      // Don't cap fraction - let it exceed 1 so later elements can complete
      if (fraction > 2) {
        cooldown = cooldownTime;
        fraction = 2;
      }

      setMorph(fraction);
    }

    function setMorph(fraction: number) {
      elementRefs.current.forEach((elementRef, index) => {
        if (!elementRef) return;

        // Find this element's position in the random order
        const orderPosition = randomOrder.indexOf(index);

        // Calculate staggered timing based on random order position
        const elementStartTime = (orderPosition * staggerDelay) / 1000; // Convert to seconds
        const adjustedFraction = Math.max(
          0,
          Math.min(1, (fraction * morphTime - elementStartTime) / morphTime),
        );

        if (adjustedFraction > 0) {
          if (adjustedFraction >= 1) {
            // Element has completed morphing - fully visible
            elementRef.style.filter = '';
            elementRef.style.opacity = '100%';

            // Mark as completed
            if (!elementCompletionRef.current[index]) {
              elementCompletionRef.current[index] = true;
              checkAllElementsComplete();
            }
          } else {
            // Apply morphing effect to this element (fade in)
            const blur = Math.min(8 / adjustedFraction - 8, 100);
            const opacity = Math.pow(adjustedFraction, 0.4) * 100;

            elementRef.style.filter = `blur(${Math.max(0, blur)}px)`;
            elementRef.style.opacity = `${Math.max(0, opacity)}%`;
          }
        } else {
          // Element hasn't started morphing yet
          elementRef.style.filter = 'blur(100px)';
          elementRef.style.opacity = '0%';
        }
      });
    }

    function doCooldown() {
      // Don't reset elements if animation is complete
      if (hasCompletedRef.current) {
        return;
      }

      elementRefs.current.forEach((elementRef, index) => {
        if (elementRef) {
          elementRef.style.filter = 'blur(100px)';
          elementRef.style.opacity = '0%';
          // Set text content only for letter elements
          if (animationElements[index].type === 'letter') {
            elementRef.textContent = animationElements[index].content as string;
          }
        }
      });

      morph = 0;
    }

    function animate() {
      // Stop animation if all elements have completed
      if (hasCompletedRef.current) {
        return;
      }

      rafRef.current = requestAnimationFrame(animate);

      const newTime = new Date();
      const dt = (newTime.getTime() - time.getTime()) / 1000;
      time = newTime;

      cooldown -= dt;

      if (cooldown <= 0) {
        doMorph();
      } else {
        doCooldown();
      }
    }

    // Start animation
    animate();

    // Cleanup function
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [animationElements, randomOrder, isVisible]);

  // Animate WordContainer, then LoaderWrapper after letter animation completes
  useEffect(() => {
    if (letterAnimationComplete) {
      // Clear any inline styles from individual elements to allow parent animation
      elementRefs.current.forEach((elementRef) => {
        if (elementRef) {
          elementRef.style.opacity = '';
          elementRef.style.filter = '';
        }
      });

      // First animate the word container
      wordControls
        .start({
          opacity: 0,
          scale: 0.5,
          transition: {
            duration: 0.3,
            ease: 'easeInOut',
          },
        })
        .then(() => {
          // Then animate the entire loader wrapper out
          return controls.start({
            opacity: 0,
            transition: {
              duration: 0.5,
              ease: 'easeInOut',
            },
          });
        })
        .then(() => {
          // Finally fade out the glass background
          glassControls.start({
            opacity: 0,
            transition: {
              duration: 0.3,
              ease: 'easeInOut',
            },
          });

          // Scale Hero image from 1.1 to 1
          return heroControls.start({
            scale: 1.1,
            transition: {
              duration: 0.8,
              ease: 'easeOut',
            },
          });
        })
        .then(() => {
          // Remove loader elements from DOM after they've faded out
          setShouldRemoveLoader(true);
        })
        .then(() => {
          // Remove has-loader class when all animations are complete
          document.body.classList.remove('has-loader');
        });
    }
  }, [
    letterAnimationComplete,
    controls,
    wordControls,
    glassControls,
    heroControls,
  ]);

  if (!isVisible) return null;

  return (
    <div id={id}>
      {!DISABLE_ANIMATIONS && !shouldRemoveLoader && (
        <>
          <GlassBackground animate={glassControls} initial={{ opacity: 1 }} />
          <LoaderWrapper animate={controls} initial={{ opacity: 1, scale: 1 }}>
            <SVGFilter>
              <defs>
                <filter id='threshold'>
                  <feColorMatrix
                    in='SourceGraphic'
                    type='matrix'
                    values='1 0 0 0 0
                            0 1 0 0 0
                            0 0 1 0 0
                            0 0 0 255 -140'
                  />
                </filter>
              </defs>
            </SVGFilter>
            <WordContainer
              animate={wordControls}
              initial={{ opacity: 1, scale: 1 }}
            >
              {animationElements.map((element, index) => (
                <MorphElement
                  key={element.key}
                  ref={(el) => {
                    elementRefs.current[index] = el;
                  }}
                >
                  {element.type === 'custom'
                    ? element.content
                    : element.content}
                </MorphElement>
              ))}
            </WordContainer>
          </LoaderWrapper>
        </>
      )}
      <ContentWrapper
        $contentWrapperBackground={config?.contentWrapperBackground}
      >
        <Hero
          slides={slides}
          imageAnimationControls={heroControls}
          config={config}
          label={label}
          pillAndTitle={pillAndTitle}
        />
      </ContentWrapper>
    </div>
  );
};

export default Loader;
